/**
 * Perfil, colección y mazo activo del jugador.
 *
 * Todo lo que hay aquí es de solo lectura, y no por falta de tiempo: el cliente
 * no tiene permiso para escribir en `profiles.gold` ni en `user_cards` (ver la
 * migración de RLS). Las mutaciones que existan llegarán como llamadas RPC, no
 * como updates.
 *
 * Los datos se piden con TanStack Query en vez de guardarse en Zustand porque
 * son *estado del servidor*: tienen dueño remoto, caducan y pueden cambiar
 * desde otro dispositivo. Zustand se queda con lo que es de verdad local — la
 * partida en curso, la carta seleccionada.
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryClient';
import { describeDatabaseError, supabase } from '@/services/supabaseClient';
import type { DbDeck, DbProfile, DbUserCard } from '@/types/database';
import type { CollectionCard, Deck, Profile } from '@/types/game';

/* -------------------------------------------------------------------------- */
/* Traducción entre la base y el dominio                                      */
/* -------------------------------------------------------------------------- */
// PostgreSQL usa snake_case y el dominio camelCase. La conversión se hace aquí,
// en la frontera, y no se deja escapar hacia los componentes: si `avatar_url` se
// colara en el JSX, renombrar una columna obligaría a tocar media interfaz.

function toProfile(row: DbProfile): Profile {
  return {
    id: row.id,
    username: row.username,
    avatarUrl: row.avatar_url,
    gold: row.gold,
    gems: row.gems,
    isVip: row.is_vip,
    vipExpiresAt: row.vip_expires_at,
    createdAt: row.created_at,
  };
}

function toCollectionCard(row: DbUserCard): CollectionCard {
  return {
    id: row.id,
    masterId: row.card_master_id,
    level: row.level,
    xp: row.xp,
    isTradable: row.is_tradable,
    serialNumber: row.serial_number,
    acquiredAt: row.created_at,
  };
}

function toDeck(row: DbDeck): Deck {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    cardIds: row.card_ids,
    isActive: row.is_active,
  };
}

/** Convierte el error de PostgREST en algo legible antes de que llegue a la UI. */
function fail(error: { code?: string; message: string }): never {
  const wrapped = new Error(describeDatabaseError(error));
  Object.assign(wrapped, { code: error.code });
  throw wrapped;
}

/* -------------------------------------------------------------------------- */
/* Consultas                                                                  */
/* -------------------------------------------------------------------------- */

/** Perfil del jugador: nombre, saldo y estado VIP. */
export function useProfile(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.profile(userId ?? 'anon'),
    enabled: Boolean(userId && supabase),
    queryFn: async (): Promise<Profile> => {
      const { data, error } = await supabase!
        .from('profiles')
        .select('*')
        .eq('id', userId!)
        .single();
      if (error) fail(error);
      return toProfile(data);
    },
  });
}

/**
 * Colección completa.
 *
 * Se trae entera de una vez y no paginada: son unas decenas de cartas al
 * principio y unos cientos en el peor caso, y tenerlas todas en memoria hace que
 * el editor de mazos y la pantalla de fusión (Fase 3) funcionen sin más
 * peticiones. Si algún día un jugador acumula miles, esto habrá que paginarlo.
 */
export function useUserInventory(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.inventory(userId ?? 'anon'),
    enabled: Boolean(userId && supabase),
    queryFn: async (): Promise<CollectionCard[]> => {
      const { data, error } = await supabase!
        .from('user_cards')
        .select('*')
        .eq('user_id', userId!)
        .order('created_at', { ascending: true });
      if (error) fail(error);
      return data.map(toCollectionCard);
    },
  });
}

/**
 * Mazo activo.
 *
 * `maybeSingle` y no `single`: no tener mazo activo es un estado posible —una
 * cuenta a la que el trigger de alta no llegó a crearle uno, o alguien que
 * desactivó el suyo— y merece un null, no una excepción.
 */
export function useActiveDeck(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.activeDeck(userId ?? 'anon'),
    enabled: Boolean(userId && supabase),
    queryFn: async (): Promise<Deck | null> => {
      const { data, error } = await supabase!
        .from('decks')
        .select('*')
        .eq('user_id', userId!)
        .eq('is_active', true)
        .maybeSingle();
      if (error) fail(error);
      return data ? toDeck(data) : null;
    },
  });
}

/**
 * Las tres cosas juntas, que es como las necesita la pantalla principal.
 *
 * Además resuelve el mazo activo a ids de carta maestra, que es lo que consume
 * `createMatch`: el motor no sabe nada de `user_cards`, solo de qué carta es
 * cada una.
 */
export function usePlayerData(userId: string | undefined) {
  const profile = useProfile(userId);
  const inventory = useUserInventory(userId);
  const deck = useActiveDeck(userId);

  const cardsById = new Map((inventory.data ?? []).map((card) => [card.id, card]));

  // Se filtran los ids que no estén en el inventario en vez de fallar: una carta
  // vendida mientras el mazo seguía en caché dejaría el mazo entero inservible.
  // El editor de mazos avisará de que faltan cartas; la pantalla no se rompe.
  const deckCards = (deck.data?.cardIds ?? [])
    .map((id) => cardsById.get(id))
    .filter((card): card is CollectionCard => card !== undefined);

  return {
    profile: profile.data ?? null,
    inventory: inventory.data ?? [],
    deck: deck.data ?? null,
    deckCards,
    // Con el nivel: una carta fusionada debe jugarse con el poder que costó
    // conseguir, también contra la IA.
    deckMasterIds: deckCards.map((card) => ({ masterId: card.masterId, level: card.level })),
    /** Falta alguna carta del mazo: se ha vendido o fusionado desde otro sitio. */
    isDeckIncomplete: Boolean(deck.data) && deckCards.length !== (deck.data?.cardIds.length ?? 0),
    isLoading: profile.isLoading || inventory.isLoading || deck.isLoading,
    error: profile.error ?? inventory.error ?? deck.error ?? null,
    refetch: () => {
      void profile.refetch();
      void inventory.refetch();
      void deck.refetch();
    },
  };
}
